// [meta] name: null
// [meta] description: Keep the same contents but add some more visual flare, while keeping it minimal

import {
  Check,
  CheckCircle,
  CircleDashed,
  CircleXIcon,
  InfoIcon,
  Loader2,
  RefreshCcw,
  X,
} from "lucide-react";
import { useEffect } from "react";
import { useState } from "react";
import React from "react";
import { Progress } from "@/components/ui/progress";
import { useSelector } from "react-redux";
import { Button } from "../ui/button";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { cn } from "@/lib/utils";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
  useNavigateToAIChat,
} from "@/store/zustand/layoutStore";
import { BouncingDotsLoader } from "../ui/bouncing-dots-loader";
import { track } from "@/utils/events/tracker";
import { show, showNewMessage } from "@intercom/messenger-js-sdk";
import { useJoinedErrors } from "./hooks/useStoryboardErrors";
import {
  getDevServerUrl,
  useActiveMainCanvas,
  useActiveProject,
  useDevServerSuffix,
} from "@/utils/utils";
import useLocalMode from "@/hooks/useLocalMode";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { ReactFramework } from "@/utils/templateProjects";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import {
  useLaunchContainer,
  useRunTerminalCommand,
  useStartServer,
} from "@/utils/devServerUtils";

const INIT_ENV_STEP = {
  order: 0,
  key: "init_env",
  text: "Initializing Environment",
  estimated_time_ms: 2000,
};

const MAX_POSSIBLE_STEPS = 6;

interface PlanStep {
  order: number;
  key: string;
  text: string;
  estimated_time_ms: number;
}

interface Props {
  hidden?: boolean;
  containerPaused?: boolean;
  devServerNotRunning?: boolean;
  terminalDisconnected?: boolean;
}

const ProjectLaunchProgress: React.FC<Props> = React.memo(
  ({ hidden, containerPaused, devServerNotRunning, terminalDisconnected }) => {
    const [planSteps, setPlanSteps] = useState<PlanStep[]>([]);
    const [expectedTotalSteps, setExpectedTotalSteps] =
      useState(MAX_POSSIBLE_STEPS);
    const [stepToStartTime, setStepToStartTime] = useState({
      0: new Date().getTime(),
    });
    const [_, setRerenderTrigger] = useState(0);

    const projectLaunchStatus = useCodebaseStore(
      (state) => state.projectLaunchStatus
    );

    const allErrors = useJoinedErrors();

    const { isLocalMode, localPort } = useLocalMode();

    const canvas = useActiveMainCanvas();
    const project = useActiveProject();
    const devServerSuffix = useDevServerSuffix();
    const devServerUrl = getDevServerUrl(
      canvas,
      devServerSuffix,
      isLocalMode,
      localPort
    );

    const setNewLayout = useLayoutStore((state) => state.setNewLayout);
    const navigateToAIChat = useNavigateToAIChat();
    const isAdmin = useFeatureFlagEnabled("is-admin");

    const projectLoadingProgress = useSelector(
      (state: any) => state.supabaseStore.projectLoadingProgress
    );

    const { triggerRefetchProjectLoadingProgress } = useSupabase();

    const [deletingNodeModules, setDeletingNodeModules] = useState(false);
    const [relaunchingContainer, setRelaunchingContainer] = useState(false);
    const runTerminalCommand = useRunTerminalCommand();
    const startServer = useStartServer();
    const launchContainer = useLaunchContainer();

    useEffect(() => {
      if (!hidden) {
        const inter = setInterval(() => {
          triggerRefetchProjectLoadingProgress();
        }, 5000);

        return () => {
          clearInterval(inter);
        };
      }
    }, [hidden]);

    useEffect(() => {
      setStepToStartTime({
        0: new Date().getTime(),
      });

      // If hidden, reset plan steps
      if (hidden) {
        setPlanSteps([]);
        return;
      }

      const inter = setInterval(() => {
        setRerenderTrigger((prev) => prev + 1);
      }, 100);

      return () => {
        // Clear the timeout
        clearInterval(inter);
      };
    }, [hidden]);

    useEffect(() => {
      // Create a plan once when the project launch status is available
      if (!projectLoadingProgress) {
        setPlanSteps([INIT_ENV_STEP]);
        return;
      }

      // Always keep the previous steps, but add any new ones that are set to false (in order)
      let newSteps: PlanStep[] = [...planSteps];
      let addedNewSteps = false;

      const addPlanStep = (step: PlanStep) => {
        if (!newSteps.find((s) => s.key === step.key)) {
          newSteps.push(step);
          addedNewSteps = true;
        }
      };

      if (!projectLoadingProgress.initialized_env) {
        addPlanStep(INIT_ENV_STEP);
      }

      if (!projectLoadingProgress.cloning_done) {
        addPlanStep({
          order: 1,
          key: "cloning_done",
          text: "Cloning",
          estimated_time_ms: 5000, // TODO: Do an intelligent estimation based on codebase
        });
      }

      if (!projectLoadingProgress.files_loaded) {
        addPlanStep({
          order: 2,
          key: "files_loaded",
          text: "Loading Codebase",
          estimated_time_ms: 4000, // TODO: Do an intelligent estimation based on codebase
        });
      }

      if (projectLoadingProgress.installed_devtools === false) {
        addPlanStep({
          order: 2,
          key: "installed_devtools",
          text: "Installing Devtools",
          estimated_time_ms: 1000, // TODO: Do an intelligent estimation based on codebase
        });
      }

      if (!projectLoadingProgress.stopped_container) {
        addPlanStep({
          order: 4,
          key: "stopped_container",
          text: "Stopping Old Container",
          estimated_time_ms: 7000,
        });
      }

      if (!projectLoadingProgress.launched_container) {
        addPlanStep({
          order: 5,
          key: "launched_container",
          text: "Launching Container",
          estimated_time_ms: 2000,
        });
      }

      if (!projectLoadingProgress.started_dev_server) {
        addPlanStep({
          order: 7,
          key: "started_dev_server",
          text: projectLoadingProgress?.command_running
            ? "Running Terminal Command"
            : "Starting Dev Server",
          estimated_time_ms: 15000,
        });
      }

      if (!projectLoadingProgress.dev_server_responding) {
        addPlanStep({
          order: 8,
          key: "dev_server_responding",
          text: "Waiting For Response",
          estimated_time_ms: 15000,
        });
      }

      newSteps = newSteps.sort((a, b) => a.order - b.order);

      if (addedNewSteps) {
        setPlanSteps(newSteps);
        setExpectedTotalSteps(newSteps.length);
      }
    }, [projectLoadingProgress]);

    let currentStepIndex = planSteps.findIndex((step, index) => {
      // Return the first step if it's not loaded
      if (!projectLoadingProgress) {
        return true;
      }

      // If the plan is created from the project loading progress, finish this step
      if (step.key === "init_env" && planSteps.length > 1) {
        return false;
      }

      return (
        !projectLoadingProgress[step.key] || index === planSteps.length - 1
      );
    });

    if (currentStepIndex === -1) {
      currentStepIndex = 0;
    }

    useEffect(() => {
      planSteps.forEach((step) => {
        if (!stepToStartTime[currentStepIndex]) {
          setStepToStartTime({
            ...stepToStartTime,
            [currentStepIndex]: new Date().getTime(),
          });
        }
      });
    }, [currentStepIndex]);

    // If a step takes longer than expected it stops at 95% of the way to the finish rather than at 100%, showing there is still some progress left
    let stepTime = stepToStartTime[currentStepIndex]
      ? Math.min(
          new Date().getTime() - stepToStartTime[currentStepIndex],
          (planSteps[currentStepIndex]?.estimated_time_ms || 1) * 0.95
        )
      : 0;

    // If step is completed -> set it to the estimated time to show it's done
    if (projectLoadingProgress?.[planSteps[currentStepIndex]?.key]) {
      stepTime = planSteps[currentStepIndex]?.estimated_time_ms || 1;
    }

    const stepProgress =
      stepTime / (planSteps[currentStepIndex]?.estimated_time_ms || 1);
    const progress =
      ((currentStepIndex + stepProgress) / expectedTotalSteps) * 100;

    const certainTerminalDisconnected =
      terminalDisconnected && !planSteps?.length;
    const certainDevServerNotRunning =
      devServerNotRunning && !planSteps?.length;

    const currentStep = planSteps[currentStepIndex]?.key;
    const [waitingDevServerStartTime, setWaitingDevServerStartTime] = useState<
      number | null
    >(null);
    useEffect(() => {
      if (currentStep === "started_dev_server") {
        setWaitingDevServerStartTime(new Date().getTime());
      } else {
        setWaitingDevServerStartTime(null);
      }
    }, [currentStep]);

    // Wait for 1 minute for the dev server to start
    // For expo it can take longer to start
    const waitTime =
      project?.framework === ReactFramework.EXPO ? 3 * 60_000 : 60_000;

    const waitingDevServerOverTime =
      waitingDevServerStartTime &&
      new Date().getTime() - waitingDevServerStartTime > waitTime;

    // For expo show warning right away with helpful message
    const waitingExpo =
      waitingDevServerStartTime && project?.framework === ReactFramework.EXPO;

    const containerPausedNotLoading = containerPaused && !currentStep;

    /**
     * Track in posthog
     */
    useEffect(() => {
      if (certainTerminalDisconnected) {
        track("TERMINAL_DISCONNECTED");
      }
    }, [certainTerminalDisconnected]);

    useEffect(() => {
      if (containerPausedNotLoading) {
        track("CONTAINER_PAUSED");
      }
    }, [containerPausedNotLoading]);

    useEffect(() => {
      if (certainDevServerNotRunning) {
        track("DEV_SERVER_NOT_RUNNING");
      }
    }, [certainDevServerNotRunning]);

    useEffect(() => {
      if (waitingDevServerOverTime) {
        track("WAITING_DEV_SERVER_LONG");
      }
    }, [waitingDevServerOverTime]);

    return (
      <div
        className={cn(
          "absolute w-full h-full text-center flex justify-center items-center py-10 space-y-4 z-[1000] bg-secondary-100",
          hidden && "hidden"
        )}
      >
        <div className="w-[400px] flex flex-col gap-9">
          <h1 className="font-medium text-[28px] leading-[34px]">
            {projectLoadingProgress?.command_running
              ? "Terminal Command"
              : certainTerminalDisconnected
              ? "Terminal Disconnected"
              : containerPausedNotLoading
              ? "Container Paused"
              : certainDevServerNotRunning
              ? "Dev Server Not Running"
              : "Loading Project"}
          </h1>
          {certainDevServerNotRunning ? (
            <div className="flex flex-col gap-2">
              <div className="text-sm text-secondary-n-400 mb-1 mt-[-1rem]">
                This is likely because the dev server crashed. Click to debug
                the issue with AI in the chat.
              </div>
              <Button
                variant="secondary"
                className="flex item-center justify-center gap-2 rounded-lg"
                onClick={() => {
                  navigateToAIChat(LayoutMode.Code);
                }}
              >
                Debug With AI
              </Button>
              <Button
                variant="secondary"
                className="flex item-center justify-center gap-2 rounded-lg"
                onClick={() => {
                  window.location.reload();
                }}
              >
                <RefreshCcw className="w-4 h-4" /> Try to Reload the Server
              </Button>
            </div>
          ) : projectLaunchStatus === "error" ||
            certainTerminalDisconnected ||
            containerPausedNotLoading ? (
            <Button
              variant="secondary"
              className="flex item-center justify-center gap-2 rounded-lg"
              onClick={() => {
                window.location.reload();
              }}
            >
              <RefreshCcw className="w-4 h-4" />
              <span>
                {certainTerminalDisconnected
                  ? "Terminal Disconnected - Create New Connection"
                  : containerPausedNotLoading
                  ? "Container Paused - Load Canvas"
                  : "Error - Reload Page"}
              </span>
            </Button>
          ) : (
            <Progress
              value={progress}
              indicatorClassname="bg-gradient-to-r from-primary-n-800 to-primary-n-600"
              className="bg-secondary-n-950 h-[10px]"
            />
          )}
          <div className="flex flex-col gap-5 items-flex-start justify-center">
            {planSteps.map((step: PlanStep, index) => (
              <React.Fragment key={step.key}>
                <div className="flex items-center justify-space-between flex-row w-full h-7">
                  <div className="flex items-center justify-start shrink-0 space-x-4 grow">
                    {index < currentStepIndex ? (
                      <div className="p-1 rounded-full bg-primary-n-900 bg-opacity-25">
                        <Check className="h-5 w-5 animate-in text-primary-n-600" />
                      </div>
                    ) : index === currentStepIndex ? (
                      projectLaunchStatus === "error" ? (
                        <div className="p-1 rounded-full bg-error-900 bg-opacity-25">
                          <X className="h-5 w-5 text-red-600" />
                        </div>
                      ) : (
                        <BouncingDotsLoader />
                      )
                    ) : (
                      <BouncingDotsLoader disabled />
                    )}
                    <p
                      className={cn(
                        "font-medium text-lg leading-6",
                        index < currentStepIndex && "text-secondary-n-400",
                        index > currentStepIndex && "text-secondary-n-600"
                      )}
                    >
                      {step.text}
                      {currentStepIndex === index ? "..." : ""}
                    </p>
                  </div>
                  <Button
                    className={cn(
                      index !== currentStepIndex && "hidden",
                      planSteps[currentStepIndex]?.key !==
                        "started_dev_server" && "hidden",
                      projectLaunchStatus === "error" && "hidden",
                      waitingDevServerOverTime && "hidden"
                    )}
                    size="sm"
                    variant={"outline"}
                    onClick={() => {
                      setNewLayout(LeftTab.AIChat, LayoutMode.Code);
                    }}
                  >
                    Logs
                  </Button>
                </div>
                {index === currentStepIndex &&
                  planSteps[currentStepIndex]?.key === "started_dev_server" &&
                  (waitingDevServerOverTime ||
                    waitingExpo ||
                    projectLaunchStatus === "error") && (
                    <div className="pl-3 flex flex-col items-start justify-center gap-2">
                      {projectLaunchStatus === "error" ? (
                        <div className="text-sm text-white flex flex-row items-center justify-start gap-2">
                          <InfoIcon className="w-4 h-4 text-red-500/70" />
                          Failed to start dev server.
                        </div>
                      ) : waitingDevServerOverTime ? (
                        <div className="text-sm text-white flex flex-row items-center justify-start gap-2">
                          <InfoIcon className="w-4 h-4 text-yellow-500/70" />
                          Starting dev server is taking longer than expected.
                        </div>
                      ) : waitingExpo ? (
                        <div className="text-sm text-white flex flex-row items-center justify-start gap-4 text-left">
                          <InfoIcon className="size-5 text-yellow-500/70" />
                          Expo apps can take a bit longer to start on first
                          load. Please wait 1-2 minutes.
                        </div>
                      ) : null}
                      <div
                        className={cn(
                          "flex flex-row items-center justify-start gap-2 flex-wrap",
                          waitingExpo && !waitingDevServerOverTime && "hidden"
                        )}
                      >
                        <Button
                          size="sm"
                          variant={"outline"}
                          onClick={() => {
                            navigateToAIChat(LayoutMode.Code);
                          }}
                        >
                          {projectLaunchStatus === "error"
                            ? "Fix with AI"
                            : "Open Terminal"}
                        </Button>
                        {devServerUrl && isAdmin && (
                          <Button
                            size="sm"
                            variant={"outline"}
                            onClick={() => {
                              window.open(devServerUrl, "_blank");
                            }}
                          >
                            Open Dev Link to Debug
                          </Button>
                        )}
                        {projectLaunchStatus === "error" && (
                          <>
                            <Button
                              size="sm"
                              variant={"outline"}
                              onClick={async () => {
                                if (
                                  deletingNodeModules ||
                                  relaunchingContainer
                                ) {
                                  return;
                                }

                                setDeletingNodeModules(true);

                                track("RE_INSTALL_NODE_MODULES");

                                try {
                                  await runTerminalCommand({
                                    command: "rm -rf node_modules",
                                  });

                                  await startServer({
                                    forceRestart: true,
                                    doNotCheckContainer: true,
                                    doNotReloadCodebase: true,
                                  });
                                } catch (error) {
                                  console.error(error);
                                } finally {
                                  setDeletingNodeModules(false);
                                }
                              }}
                            >
                              {deletingNodeModules && (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              )}
                              <span>Re-Install Node Modules</span>
                            </Button>
                            <Button
                              size="sm"
                              variant={"outline"}
                              onClick={async () => {
                                if (
                                  deletingNodeModules ||
                                  relaunchingContainer
                                ) {
                                  return;
                                }

                                setRelaunchingContainer(true);

                                track("RE_LAUNCH_CONTAINER");

                                try {
                                  await launchContainer({
                                    forceRelaunch: true,
                                  });
                                } catch (error) {
                                  console.error(error);
                                } finally {
                                  setRelaunchingContainer(false);
                                }
                              }}
                            >
                              {relaunchingContainer && (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              )}
                              <span>Relaunch Container</span>
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant={"outline"}
                          onClick={() => {
                            showNewMessage(
                              `I'm having trouble starting my dev server. My canvas link is https://app.tempo.new/canvases/${canvas.id}/editor. Can you help me?`
                            );
                          }}
                        >
                          Get Help
                        </Button>
                      </div>
                    </div>
                  )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

export default ProjectLaunchProgress;
